import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, CheckCircle2, X, Loader2, RefreshCw, AlertTriangle, Mail, User, ChevronLeft, PenLine,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import CountersignatureSetup from './CountersignatureSetup';
import { takeDeepLink } from './deepLink';

const API = '/api';

const STATUS_META = {
  pending_review: { label: 'Pending review', cls: 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10' },
  approved:       { label: 'Approved',       cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  declined:       { label: 'Declined',       cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
};

const FILTERS = ['pending_review', 'approved', 'declined', 'all'];

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const feeLabel = (choice) =>
  choice === 'monthly' ? '$57.50 / month'
    : choice === 'annual' ? '$690 / year'
      : (choice || '—');

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

// --- module-level subcomponents (stable types; avoid remount/focus loss) ---

// A FastAPI 422 `detail` is a LIST of validation objects, not a string. Interpolating it
// gives "[object Object]" - which is exactly what Randall saw when the resend preview failed,
// destroying the one message that named the cause. Anything non-string is rendered, never
// interpolated.
function errText(e, fallback) {
  const d = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d.map((x) => {
      if (typeof x === 'string') return x;
      const where = Array.isArray(x?.loc) ? x.loc.join('.') : '';
      const msg = x?.msg || x?.type || JSON.stringify(x);
      return where ? `${where}: ${msg}` : msg;
    }).filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  if (d && typeof d === 'object') {
    try { return JSON.stringify(d); } catch { /* fall through */ }
  }
  return fallback || 'Something went wrong.';
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  return <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function FlagChip({ tone, title, children }) {
  const cls = tone === 'red'
    ? 'text-red-400 border-red-500/40 bg-red-500/10'
    : 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10';
  return (
    <span title={title} className={`inline-flex items-center gap-1 text-[10px] font-sans rounded-sm px-1.5 py-0.5 border ${cls}`}>
      <AlertTriangle size={10} /> {children}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-hg-text-dim font-sans">{label}</div>
      <div className="text-sm font-sans text-hg-text break-words">{value ?? '—'}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
      <div className="text-xs font-sans text-hg-text uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}

function ConsentLine({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm font-sans">
      {ok
        ? <CheckCircle2 size={14} className="text-hg-green" />
        : <X size={14} className="text-hg-text-dim" />}
      <span className="text-hg-text">{label}:</span>
      <span className={ok ? 'text-hg-green' : 'text-hg-text-dim'}>{ok ? 'Authorized' : 'Not'}</span>
    </div>
  );
}

function AgreementCard({ agreement, onOpen }) {
  // The LIST endpoint returns these FLAT (contact_name, business_name, email); only the
  // DETAIL endpoint nests them under `account`. This card reads the list payload, and
  // reading `agreement.account.contact_name` here made every row with a blank business name
  // render "(no name)" - Alton Labrecque among them, so Randall had to open a row to see
  // whose it was. Both shapes are accepted rather than assumed.
  const acct = agreement.account || agreement || {};
  const name = acct.contact_name || acct.business_name || agreement.contact_name
    || agreement.business_name || '(no name)';
  const when = agreement.signed_at || agreement.created_at;
  return (
    <button
      data-testid={`ai-card-${agreement.agreement_id}`}
      onClick={() => onOpen(agreement.agreement_id)}
      className="w-full text-left bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2 hover:border-hg-green transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-sans text-hg-text flex items-center gap-1.5">
            <User size={13} className="text-hg-green" /> {name}
          </div>
          {(acct.email || agreement.email) && (
            <div className="text-[11px] font-sans text-hg-text-dim flex items-center gap-1 mt-0.5">
              <Mail size={11} /> {acct.email || agreement.email}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <StatusBadge status={agreement.status} />
          {(agreement.resign || agreement.resign_fitter_id) && <FlagChip tone="amber" title="Replacement licence on an existing active fitter's record">Re-sign</FlagChip>}
          {agreement.needs_review && <FlagChip tone="amber" title={agreement.review_reason}>Needs review</FlagChip>}
          {agreement.suppression?.suppressed && <FlagChip tone="red" title="Suppressed">Suppressed</FlagChip>}
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap text-[11px] font-sans text-hg-text-dim">
        <span>{fmtDate(when)}</span>
        <span>· {feeLabel(agreement.fee_choice)}</span>
        {agreement.reference && <span>· ref {agreement.reference}</span>}
      </div>
    </button>
  );
}

// --- detail view ---

// Re-sign (28 Oct 2026): what approving a replacement licence does, and — once approved and
// until the card lands — the card link's state with a reissue. The link lives on the agreement
// (never the fitter), so its reissue is here rather than on the onboarding screen.
function ResignSection({ agreement, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const link = agreement.card_link || {};
  const approved = agreement.status === 'approved';
  const carded = !!agreement.card_on_file_at;
  const reissue = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await axios.post(`${API}/agreements/${agreement.agreement_id}/card-link/resend`);
      setMsg(`New card link sent to ${r.data?.sent_to} — expires ${String(r.data?.expires_at || '').slice(0, 10)}. The previous link no longer works.`);
      onChanged?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not reissue the card link.');
    }
    setBusy(false);
  };
  return (
    <Section title="Re-sign — existing fitter">
      <div className="text-sm font-sans text-hg-text space-y-2" data-testid="ag-resign">
        <p>
          A replacement Fitter License &amp; Account Agreement on an <b>existing active</b> fitter&apos;s record.
          Approving countersigns it and emails a card link. The fitter stays active: no new record, no welcome
          packet, no list changes. The new card replaces the old one, which is kept on the record.
        </p>
        {approved && (
          <p className="text-hg-text-dim" data-testid="ag-resign-card">
            {carded ? `Card on file ${String(agreement.card_on_file_at).slice(0, 10)} (…${agreement.card_last_four || ''}).`
              : `Card link: ${link.status || 'none'}${link.expires_at ? `, expires ${String(link.expires_at).slice(0, 10)}` : ''}.`}
          </p>
        )}
        {approved && !carded && (
          <button data-testid="ag-resign-reissue" onClick={reissue} disabled={busy}
            className="px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm hover:border-hg-green disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Reissue card link'}
          </button>
        )}
        {msg && <div className="text-xs text-hg-text-dim">{msg}</div>}
      </div>
    </Section>
  );
}

const fmtDay = (s) => (s ? String(s).slice(0, 10) : '—');

// Certification that predates the Hub is ATTESTED (Randall, 28 Oct 2026): recorded as who,
// when and the words they cited — and shown as an attestation, never as an exam record. The
// record must let anyone tell "Randall attested this on 28 Oct" from "she sat the exam then".
function OnboardingRecord({ fitterId, agreement }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState('');
  const [attesting, setAttesting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setSnap((await axios.get(`${API}/onboarding/${fitterId}`))?.data || null); }
    catch { setSnap(null); }
  }, [fitterId]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    setBusy(true); setErr('');
    try { setSnap((await axios.post(`${API}/onboarding/${fitterId}/${path}`, body))?.data || snap); setAttesting(false); setNote(''); }
    catch (e) { setErr(e.response?.data?.detail || 'That did not go through.'); }
    setBusy(false);
  };

  if (!snap) return null;
  const cert = snap.certification || {};
  const ds = snap.demo_system || {};
  const history = snap.demo_system_history || [];
  const p2 = (snap.packets || {}).packet_2 || {};
  const owedBuild = agreement?.resign_fitter_id && agreement?.card_on_file_at && !agreement?.build_queued_at;

  return (
    <Section title="Onboarding record">
      <div className="space-y-3 text-sm font-sans" data-testid="ob-record">
        <div data-testid="ob-cert">
          <div className="text-[10px] uppercase tracking-wider text-hg-text-dim">Certification</div>
          {cert.basis === 'recorded' && (
            <div>Certified on record — portal {fmtDay(cert.portal_completed_at)}, exam {fmtDay(cert.exam_passed_at)}.</div>
          )}
          {cert.basis === 'attested' && (
            <div className="border border-dashed border-hg-border rounded-sm px-3 py-2 mt-1" data-testid="ob-attestation">
              <div className="text-[10px] uppercase tracking-wider text-hg-text-dim">Attestation — not an exam record</div>
              <div>Attested by {cert.attested_by} on {fmtDay(cert.attested_at)}. No portal or exam date is on record.</div>
              {cert.attestation_note && <div className="text-hg-text-dim mt-1 whitespace-pre-wrap">{cert.attestation_note}</div>}
            </div>
          )}
          {!cert.basis && (
            <div className="text-hg-text-dim">Not on record ({cert.status || 'not_started'}).</div>
          )}
          {!cert.basis && !attesting && (
            <button data-testid="ob-attest-open" onClick={() => setAttesting(true)}
              className="mt-1 text-[11px] underline text-hg-text-dim hover:text-hg-text">
              Certified before the Hub kept records? Attest it…
            </button>
          )}
          {!cert.basis && attesting && (
            <div className="mt-2 space-y-2" data-testid="ob-attest-form">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="What it rests on — e.g. the register entries and their dates."
                data-testid="ob-attest-note"
                className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text" />
              <p className="text-[11px] text-hg-text-dim">
                Recorded as an attestation under your name and today&apos;s date — never as portal or exam dates.
                It lets a replacement build queue; it does not make Packet 2 due.
              </p>
              <div className="flex gap-2">
                <button data-testid="ob-attest-confirm" disabled={busy || note.trim().length < 10}
                  onClick={() => post('certification/attest', { note: note.trim() })}
                  className="px-3 py-1.5 text-xs border border-hg-border rounded-sm hover:border-hg-green disabled:opacity-40">Attest</button>
                <button onClick={() => { setAttesting(false); setNote(''); }} className="px-3 py-1.5 text-xs text-hg-text-dim">Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div data-testid="ob-packet2" className="text-hg-text-dim">
          Packet 2: {p2.sent_at ? `sent ${fmtDay(p2.sent_at)}` : p2.due ? 'due' : 'not due'}
          {cert.basis === 'attested' && ' — certified before the Hub, so it was sent then'}
        </div>

        <div data-testid="ob-build">
          <div className="text-[10px] uppercase tracking-wider text-hg-text-dim">Demo system</div>
          <div>Current: {ds.status}{ds.queued_at ? ` · queued ${fmtDay(ds.queued_at)}` : ''}{ds.note ? ` — ${ds.note}` : ''}</div>
          {owedBuild && (
            <div className="mt-1 space-y-1">
              <div className="text-amber-300 text-[12px]">
                The card is on file but the replacement build did not queue: {agreement.build_not_queued_reason || 'see the log'}
              </div>
              <button data-testid="ob-queue-replacement" disabled={busy} onClick={() => post('build/replacement')}
                className="px-3 py-1.5 text-xs border border-hg-border rounded-sm hover:border-hg-green disabled:opacity-40">
                Queue the replacement build
              </button>
            </div>
          )}
          {history.length > 0 && (
            <ul className="mt-1 text-[12px] text-hg-text-dim space-y-0.5" data-testid="ob-build-history">
              {history.map((h, i) => (
                <li key={i}>Previous cycle: {h.status}{h.arrived_at ? `, arrived ${fmtDay(h.arrived_at)}` : ''} — archived {fmtDay(h.archived_at)}: {h.archived_reason}</li>
              ))}
            </ul>
          )}
        </div>
        {err && <div className="text-xs text-red-400">{err}</div>}
      </div>
    </Section>
  );
}

function AgreementDetail({ id, actor, onBack, onChanged }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [posting, setPosting] = useState('');       // '' | 'approve' | 'decline'
  const [actionError, setActionError] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [acctNumber, setAcctNumber] = useState('');
  const [regionChoice, setRegionChoice] = useState('');
  const [regionSaving, setRegionSaving] = useState(false);
  // Approving EXECUTES the agreement on Henry-Griffitts' behalf, so it is deliberately a
  // two-step: the first click reveals what is about to happen, the second does it. The
  // same reasoning as typed initials — nobody should sign a contract by clicking a button
  // whose label does not mention signing.
  const [confirmExec, setConfirmExec] = useState(false);
  const [countersig, setCountersig] = useState(null);
  const [heldNotice, setHeldNotice] = useState('');
  // RESEND EXECUTED COPY. It sends a real email to a real licensee under Randall's name, so
  // it is built like it can hurt: disabled unless the agreement is genuinely executed, the
  // preview is mandatory, the recipient is on screen in full, there is a confirm step, and
  // the outcome stays put instead of navigating away.
  const [rsNote, setRsNote] = useState('');
  const [rsPreview, setRsPreview] = useState(null);   // null until he has SEEN it
  const [rsBusy, setRsBusy] = useState('');
  const [rsConfirm, setRsConfirm] = useState(false);
  const [rsResult, setRsResult] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/countersignature`)
      .then((r) => { if (alive) setCountersig(r.data); })
      .catch(() => { if (alive) setCountersig({ configured: false }); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/agreements/${id}`);
      const doc = r.data?.agreement || r.data;
      setAgreement(doc);
      setAcctNumber(doc?.hg_account_number || '');
      // Prefill ONLY from an existing override. A derived region must not appear in the select
      // as though somebody chose it — the panel above already states where it came from.
      setRegionChoice(doc?.region?.overridden ? doc.region.region : '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load agreement.');
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setPosting('approve'); setActionError('');
    try {
      const num = acctNumber.trim();
      const r = await axios.post(`${API}/agreements/${id}/approve`,
        num ? { actor, hg_account_number: num } : { actor });
      onChanged?.();
      // THE EXECUTED-COPY EMAIL MAY HAVE BEEN HELD. If it was, do NOT navigate away — the
      // agreement is executed but the licensee has been told nothing, and a screen that
      // returns to the queue as usual reads as "sent". That is the silent-success shape
      // inverted into a silent non-event, and it is how HG-CTE0N-26 happened in reverse.
      const held = r?.data?.executed?.held;
      if (held) { setHeldNotice(held); setPosting(''); setConfirmExec(false); return; }
      onBack();
    } catch (e) {
      setActionError(errText(e, 'Approve failed.'));
      setPosting('');
      setConfirmExec(false);
    }
  };

  // Editable on an approved record too, not just at approval: a fitter approved before this
  // field existed has no override, and a fitter who moves needs it changed.
  const saveRegion = async (value) => {
    const fitterId = agreement?.fitter_id_resolved || agreement?.fitter_id;
    if (!fitterId) { setActionError('No fitter record yet — the region can be set after signing.'); return; }
    setRegionSaving(true); setActionError('');
    try {
      // '' clears the override and returns to deriving from the country.
      await axios.post(`${API}/onboarding/${fitterId}/region`, { region: value || null });
      await load();
    } catch (e) {
      setActionError(errText(e, 'Could not save the region.'));
    }
    setRegionSaving(false);
  };

  // Reissue the card-setup link. The link now expires, and this is the reason that was safe
  // to do: without a one-click reissue, an expired link means a fitter stuck at "awaiting
  // card" forever and no route back except editing the database by hand.
  const resendCardLink = async () => {
    const fitterId = agreement?.fitter_id_resolved || agreement?.fitter_id;
    if (!fitterId) return;
    setResending(true); setActionError(''); setResendNote('');
    try {
      const r = await axios.post(`${API}/onboarding/${fitterId}/card-link/resend`);
      const to = r.data?.sent_to || 'the fitter';
      const exp = r.data?.expires_at ? String(r.data.expires_at).slice(0, 10) : '';
      // Say the old link is dead. Reissuing ROTATES the token, so a fitter holding the
      // previous email will get a dead page — better they hear it from Randall first.
      setResendNote(`A new link was emailed to ${to}${exp ? `, good until ${exp}` : ''}. Any earlier link has stopped working.`);
      await load();
    } catch (e) {
      setActionError(errText(e, 'Could not send a new card-setup link.'));
    }
    setResending(false);
  };

  // Preview and send hit the SAME endpoint; only `preview` differs, and the server renders
  // both from one function. A preview built by a second path is a picture of an email
  // nobody receives.
  const doPreview = async () => {
    setRsBusy('preview'); setRsResult(null);
    try {
      const r = await axios.post(`${API}/agreements/${id}/resend-executed`,
        { actor, note: rsNote, preview: true });
      setRsPreview(r.data);
    } catch (e) {
      // A failed PREVIEW is not a failed send. Nothing was being sent, and telling him
      // "NOT SENT" for a button labelled Preview is its own small lie.
      setRsResult({ ok: false, kind: 'preview',
                    detail: errText(e, 'Could not render the preview.') });
    }
    setRsBusy('');
  };

  const doResend = async () => {
    setRsBusy('send');
    try {
      const r = await axios.post(`${API}/agreements/${id}/resend-executed`,
        { actor, note: rsNote, preview: false });
      // The outcome STAYS ON SCREEN. A handler that discards the response and moves on is
      // how a failed send reads as a successful one.
      setRsResult({ ok: !!r.data?.success, kind: 'send', to: r.data?.sent_to,
                    campaign_id: r.data?.campaign_id, at: new Date().toLocaleString(),
                    detail: r.data?.success ? null : 'The send did not complete.' });
      setRsConfirm(false);
    } catch (e) {
      setRsResult({ ok: false, kind: 'send', detail: errText(e, 'Send failed.') });
      setRsConfirm(false);
    }
    setRsBusy('');
  };

  const decline = async () => {
    setPosting('decline'); setActionError('');
    try {
      await axios.post(`${API}/agreements/${id}/decline`, { actor, reason });
      onChanged?.();
      onBack();
    } catch (e) {
      setActionError(e.response?.data?.detail || 'Decline failed.');
      setPosting('');
    }
  };

  // The PDF route is AUTH-GATED (/api/agreements/{id}/pdf) — an iframe/new-tab load
  // can't carry the Clerk header, so fetch it as a blob via axios (the token interceptor
  // applies) and view the object URL. The old tokenless public route is gone.
  // (Hook lives ABOVE the early returns — rules of hooks.)
  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    axios.get(`/api/agreements/${id}/pdf`, { responseType: 'blob' })
      .then((res) => {
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      })
      .catch(() => setPdfUrl(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);

  if (loading) return (
    <div data-testid="ai-detail">
      <button data-testid="ai-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
      <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
    </div>
  );
  if (error || !agreement) return (
    <div data-testid="ai-detail" className="space-y-4">
      <button data-testid="ai-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
      <div className="text-xs text-red-400 font-sans">{error || 'Agreement not found.'}</div>
    </div>
  );

  const acct = agreement.account || {};
  const initials = agreement.initials || {};
  const sup = agreement.suppression || {};
  const isPending = agreement.status === 'pending_review';
  const cardLink = agreement.card_link;
  // Mirrors agreements_model.format_address on the backend — postal order, state and
  // postcode together, and a "state" that merely repeats the country dropped. The signing
  // form used to label that field "State / Country", so stored records carry the country
  // there; comma-joining every part printed it twice with no state at all.
  const formatAddress = (line1, city, state, postal, country) => {
    const st = (state || '').trim();
    const co = (country || '').trim();
    const stateOut = st && co && st.toLowerCase() === co.toLowerCase() ? '' : st;
    const region = [stateOut, (postal || '').trim()].filter(Boolean).join(' ');
    return [(line1 || '').trim(), (city || '').trim(), region, co].filter(Boolean).join(', ');
  };
  const shippingAddress = formatAddress(acct.address_line1, acct.city, acct.state,
                                        acct.postal_code, acct.country);

  return (
    <div data-testid="ai-detail" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button data-testid="ai-back" onClick={onBack} className="inline-flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text font-sans"><ChevronLeft size={13} /> Back</button>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={agreement.status} />
          {(agreement.resign || agreement.resign_fitter_id) && <FlagChip tone="amber" title="Replacement licence on an existing active fitter's record">Re-sign</FlagChip>}
          {agreement.needs_review && <FlagChip tone="amber" title={agreement.review_reason}>Needs review</FlagChip>}
          {sup.suppressed && <FlagChip tone="red" title="Suppressed">Suppressed</FlagChip>}
        </div>
      </div>

      {/* Account & contact */}
      <Section title="Account & contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Business name" value={acct.business_name} />
          <Field label="Contact name" value={acct.contact_name} />
          <Field label="Email" value={acct.email} />
          <Field label="Phone" value={acct.phone} />
          <Field label="Course or club" value={acct.course_or_club} />
          <Field label="Authorized fitters" value={acct.authorized_fitters} />
          <Field label="Shipping address" value={shippingAddress || '—'} />
          {/* Only rendered when they differ. A "Billing: same as shipping" field on every
              record is a line you learn to skip — and this is the line that matters on the
              one occasion it says something else. */}
          {acct.billing_same_as_shipping === false && (
            <Field
              label="Billing address (different)"
              value={formatAddress(acct.billing_address_line1, acct.billing_city,
                                   acct.billing_state, acct.billing_postal_code,
                                   acct.billing_country) || '—'}
            />
          )}
          <Field label="Business type" value={acct.business_type} />
          <Field label="Accepts referrals" value={yesNo(acct.accepts_referrals)} />
        </div>
      </Section>

      {/* Fee & authorizations */}
      <Section title="Fee & authorizations">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Fee choice" value={feeLabel(agreement.fee_choice)} />
          <Field label="Onsite training" value={agreement.onsite_training_optin ? 'Opted in' : 'No — remote + portal'} />
        </div>
        {agreement.onsite_training_optin && (
          <div className="text-sm font-sans text-yellow-300 flex items-start gap-2 pt-1">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span><b>Onsite training opted in</b> — coordinate scheduling and bill $1,500 (lower 48; +travel outside). Manual — not auto-charged.</span>
          </div>
        )}
        <div className="space-y-1.5 pt-1">
          <ConsentLine ok={!!agreement.card_on_file_consent} label="Card on file" />
          <ConsentLine ok={!!agreement.background_check_consent} label="Background check" />
        </div>
      </Section>

      {/* Initials */}
      <Section title="Initials">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Fee choice" value={initials.fee_choice} />
          <Field label="Authorizations" value={initials.authorizations} />
          <Field label="Return of system" value={initials.return_of_system} />
        </div>
      </Section>

      {/* Kit checklist */}
      <Section title="Kit checklist">
        {(agreement.kit_checklist || []).length === 0 ? (
          <div className="text-sm text-hg-text-dim font-sans">No kit items.</div>
        ) : (
          <ul className="space-y-1.5">
            {(agreement.kit_checklist || []).map((k, i) => (
              <li key={i} className="flex items-center gap-2 text-sm font-sans">
                {k?.received
                  ? <CheckCircle2 size={14} className="text-hg-green" />
                  : <span className="inline-block w-3.5 h-3.5 border border-hg-border rounded-sm" />}
                <span className="text-hg-text">{k?.item}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* RE-SIGN: a replacement licence on an existing ACTIVE fitter's record. Says what
          approving does here, before the click, because it differs from a new fitter. */}
      {agreement.resign_fitter_id && <ResignSection agreement={agreement} onChanged={onChanged} />}

      {/* The fitter's onboarding record: certification (recorded, or ATTESTED — never the same
          thing), Packet 2, and every demo-system cycle including archived ones. */}
      {agreement.fitter_id_resolved && (
        <OnboardingRecord fitterId={agreement.fitter_id_resolved} agreement={agreement} />
      )}

      {/* Flags */}
      {(agreement.needs_review || sup.suppressed) && (
        <Section title="Flags">
          {agreement.needs_review && (
            <div className="text-sm font-sans text-yellow-300 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span><b>Needs review:</b> {agreement.review_reason || '—'}</span>
            </div>
          )}
          {sup.suppressed && (
            <div className="text-sm font-sans text-red-400 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span><b>Suppressed:</b> {Array.isArray(sup.reasons) ? sup.reasons.join(', ') : (sup.reasons || '—')}</span>
            </div>
          )}
        </Section>
      )}

      {/* Signed PDF */}
      <Section title="Signed agreement">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] font-sans text-hg-text-dim flex items-center gap-1.5"><FileText size={12} /> Viewable here without leaving the Hub.</span>
          {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-[11px] font-sans text-hg-green hover:underline">Open in new tab</a>}
        </div>
        {pdfUrl ? (
          <iframe
            title="Signed agreement"
            src={pdfUrl}
            style={{ width: '100%', height: '600px', border: '1px solid var(--hg-border, #2a2a2a)' }}
          />
        ) : (
          <div className="text-[11px] font-sans text-hg-text-dim py-6 text-center">Loading the signed PDF…</div>
        )}
      </Section>

      {/* Actions */}
      {actionError && <div className="text-xs text-red-400 font-sans" data-testid="ai-action-error">{actionError}</div>}

      {/* HG account number + price-list region.
          Opaque hex inline rather than Tailwind alpha tints: this is a LIGHT theme (#FAFAF7 /
          #FFFFFF), and an alpha green on white renders as pale-on-pale. Every pair below was
          checked arithmetically - text >= 4.5:1, borders >= 3:1 (WCAG 1.4.11), because the
          border carries the state at a glance. */}
      <Section title="Account number & price-list region">
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">
              HG account number <span className="text-hg-green">— assigned by the Hub</span>
            </label>
            <input
              data-testid="ai-account-number"
              value={acctNumber}
              onChange={(e) => setAcctNumber(e.target.value)}
              placeholder="From the account book"
              className="w-full max-w-[240px] bg-white border border-hg-border rounded-sm px-3 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 text-base sm:text-xs font-sans text-hg-text"
            />
            <p className="text-[11px] font-sans text-hg-text-dim mt-1">
              {agreement.hg_account_number
                ? 'Already assigned to this fitter.'
                : `Leave blank and the Hub assigns ${agreement.hg_account_number_next || 'the next number'} when you countersign. Type one only to override.`}
            </p>
            {/* The number the Hub WILL assign, shown before he countersigns rather than
                discovered on the packet. Not a reservation — two screens open at once show
                the same value and only one gets it, because reserving on screen-open would
                burn a number out of a hand-reconciled ledger sequence. */}
            {isPending && !agreement.hg_account_number && agreement.hg_account_number_next && (
              <p data-testid="ai-account-number-next"
                 className="text-xs font-sans text-hg-green mt-1.5">
                Will be assigned: <b>{agreement.hg_account_number_next}</b>
              </p>
            )}
          </div>

          {agreement.region && (
            <div>
              <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">
                Price-list region
              </label>
              {/* The resolution and WHY, never a bare region code. */}
              <div
                data-testid="ai-region-resolved"
                className="rounded-sm px-3 py-2 text-xs font-sans"
                style={agreement.region.region === 'unknown'
                  ? { background: '#FDF6E3', border: '1px solid #A9832B', color: '#7A5B12' }
                  : { background: '#EFF7EF', border: '1px solid #5E9A62', color: '#1F5F22' }}
              >
                {agreement.region.region === 'unknown' ? (
                  <>
                    <b>No price-list region yet</b> - {agreement.region.reason}. Packet 2 will say
                    the list is on its way rather than send the wrong one. Set it below.
                  </>
                ) : (
                  <>
                    <b>{agreement.region.label}</b> - {agreement.region.reason}
                    {agreement.region.document && <> - {agreement.region.document}</>}
                    {!agreement.region.price_list_available && <> (not uploaded yet)</>}
                  </>
                )}
              </div>

              <select
                data-testid="ai-region-select"
                value={regionChoice}
                disabled={regionSaving}
                onChange={(e) => { setRegionChoice(e.target.value); saveRegion(e.target.value); }}
                className="mt-2 w-full sm:w-auto bg-white border border-hg-border rounded-sm px-3 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 text-base sm:text-xs font-sans text-hg-text disabled:opacity-50"
              >
                <option value="">Use the country on the agreement</option>
                {(agreement.region_choices || []).map((c) => (
                  <option key={c.value} value={c.value}>Set manually: {c.label}</option>
                ))}
              </select>
              {regionSaving && <span className="ml-2 text-[11px] font-sans text-hg-text-dim">Saving…</span>}

              {agreement.region.overridden && (
                <p
                  data-testid="ai-region-provenance"
                  className="text-[11px] font-sans mt-1 rounded-sm px-2 py-1 inline-block"
                  style={{ background: '#F4F0FB', border: '1px solid #8A72C0', color: '#6B4E9E' }}
                >
                  Set manually by {agreement.region.set_by || 'unknown'}
                  {agreement.region.set_at && <> on {agreement.region.set_at.slice(0, 10)}</>}
                  . A manual region overrides the country.
                </p>
              )}
            </div>
          )}
        </div>
      </Section>

      {/* THE EXECUTED-COPY EMAIL WAS HELD. Loud, persistent, and it does not clear itself:
          the agreement IS executed, and the licensee has been told nothing. Without this on
          screen Randall returns to the queue and reasonably assumes it went — the same
          silent-success failure as HG-CTE0N-26, inverted into a silent non-event. */}
      {heldNotice && (
        <div className="border-2 border-amber-500 bg-amber-400/15 rounded-sm p-4 space-y-2"
             data-testid="ai-executed-held">
          <div className="text-sm font-sans text-amber-800 font-semibold inline-flex items-center gap-2">
            <AlertTriangle size={15} /> Approved — but the executed-copy email was NOT sent
          </div>
          <p className="text-xs font-sans text-hg-text leading-relaxed">{heldNotice}</p>
          <p className="text-xs font-sans text-hg-text">
            The agreement is countersigned and the executed PDF is on file. Nothing has gone
            to the licensee. <b>Send it yourself</b>, or have the hold lifted
            (<code className="text-[11px]">EXECUTED_EMAIL_ENABLED</code>) once the
            countersignature-delivery fix is verified.
          </p>
          <button
            data-testid="ai-executed-held-ack"
            onClick={() => { setHeldNotice(''); onBack(); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-amber-600 text-amber-800 rounded-sm hover:bg-amber-400/20"
          >
            I understand — back to the queue
          </button>
        </div>
      )}

      {isPending ? (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3">
          {countersig && !countersig.configured && (
            <div className="border border-amber-400/50 bg-amber-400/10 rounded-sm p-3 text-xs font-sans text-amber-200"
                 data-testid="ai-no-countersignature">
              <b>No Henry-Griffitts countersignature on file.</b> Approving signs this
              agreement on the company&rsquo;s behalf, so approvals are blocked until one is
              captured — see Countersignature on the Approvals list.
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="ai-approve"
              onClick={() => setConfirmExec(true)}
              disabled={!!posting || confirmExec || (countersig && !countersig.configured)}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            >
              <PenLine size={13} /> Countersign &amp; approve
            </button>
            <button
              data-testid="ai-decline"
              onClick={() => setDeclineOpen((o) => !o)}
              disabled={!!posting}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans border border-red-400/50 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40"
            >
              <X size={13} /> Decline
            </button>
          </div>

          {confirmExec && (
            <div className="border border-hg-green/60 bg-hg-green/10 rounded-sm p-3 space-y-3"
                 data-testid="ai-approve-confirm">
              <div className="text-xs font-sans text-hg-text space-y-2">
                <p className="font-semibold">
                  This signs the agreement on behalf of Henry-Griffitts.
                </p>
                <p className="text-hg-text-dim">
                  Your stored signature
                  {countersig?.signer_name ? ` (${countersig.signer_name}, ${countersig.signer_title})` : ''}
                  {' '}is applied to{' '}
                  <b className="text-hg-text">{agreement.reference}</b>, the agreement becomes
                  executed by both parties, and{' '}
                  <b className="text-hg-text">
                    {agreement.account?.contact_name || agreement.account?.business_name || 'the licensee'}
                  </b>{' '}
                  {agreement.executed_email_enabled === false
                    ? ' is NOT emailed - the executed-copy email is currently held, so you '
                      + 'will need to send it yourself from this screen afterwards.'
                    : ' is emailed the fully-executed copy.'}
                  {' '}The fitter then moves to awaiting card — nothing enrolls or activates yet.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  data-testid="ai-approve-confirm-yes"
                  onClick={approve}
                  disabled={!!posting}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
                >
                  {posting === 'approve' ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  Sign &amp; send the executed copy
                </button>
                <button
                  data-testid="ai-approve-confirm-no"
                  onClick={() => setConfirmExec(false)}
                  disabled={!!posting}
                  className="px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green/50 disabled:opacity-40"
                >
                  Not yet
                </button>
              </div>
            </div>
          )}
          {declineOpen && (
            <div className="space-y-2 border-t border-hg-border pt-3">
              <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Decline reason (optional)</label>
              <input
                data-testid="ai-decline-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this being declined?"
                className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
              />
              <button
                onClick={decline}
                disabled={!!posting}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans border border-red-400/50 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40"
              >
                {posting === 'decline' ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Confirm decline
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className={`rounded-sm px-4 py-3 text-sm font-sans border ${
            agreement.status === 'approved'
              ? 'text-hg-green border-hg-green/30 bg-hg-green/10'
              : 'text-red-400 border-red-500/30 bg-red-500/10'
          }`} data-testid="ai-readonly-banner">
            {agreement.status === 'approved' ? 'Approved' : 'Declined'}
            {agreement.reviewed_by ? ` by ${agreement.reviewed_by}` : ''}
            {agreement.reviewed_at ? ` on ${fmtDate(agreement.reviewed_at)}` : ''}
            {agreement.status === 'declined' && agreement.review_reason ? ` — ${agreement.review_reason}` : ''}
          </div>

          {/* RESEND THE EXECUTED COPY — a real email to a real licensee, from Randall. */}
          {agreement.status === 'approved' && (() => {
            const executed = !!agreement.countersigned_at
              && !!(agreement.signed_pdf_public_id || agreement.signed_pdf_url);
            const why = !agreement.countersigned_at
              ? 'This agreement has not been countersigned by Henry-Griffitts.'
              : 'There is no signed PDF on file for this agreement.';
            const to = agreement.account?.email || '';
            return (
              <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3"
                   data-testid="ai-resend-executed">
                <div className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">
                  Re-send the executed copy
                </div>

                {!executed ? (
                  // Disabled WITH THE REASON BESIDE IT. A button that looks live and then
                  // errors teaches people to click and hope.
                  <div className="text-xs font-sans text-hg-text-dim" data-testid="ai-resend-blocked">
                    <b className="text-hg-text">Not available.</b> {why} Only a fully-executed
                    agreement can be sent as one.
                  </div>
                ) : (
                  <>
                    <textarea
                      data-testid="ai-resend-note"
                      value={rsNote}
                      onChange={(e) => { setRsNote(e.target.value); setRsPreview(null); }}
                      rows={5}
                      placeholder="Optional note — goes at the top of the email, above the standard text. Your sign-off is added at the end."
                      className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-2 text-[13px] font-sans text-hg-text leading-relaxed"
                    />

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        data-testid="ai-resend-preview"
                        onClick={doPreview}
                        disabled={!!rsBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green/50 disabled:opacity-40"
                      >
                        {rsBusy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                        {rsPreview ? 'Re-render preview' : 'Preview the email'}
                      </button>
                      {/* THE RECIPIENT, IN FULL, NEXT TO THE SEND CONTROL. The one thing that
                          must not go wrong is this landing in a stranger's inbox. */}
                      <span className="text-xs font-sans text-hg-text" data-testid="ai-resend-to">
                        To: <b>{to || '(no email on this agreement)'}</b>
                      </span>
                    </div>

                    {rsPreview && (
                      <div className="space-y-2" data-testid="ai-resend-preview-pane">
                        <div className="text-[11px] font-sans text-hg-text-dim">
                          Subject: <b className="text-hg-text">{rsPreview.subject}</b>
                        </div>
                        <iframe
                          title="Executed copy preview"
                          data-testid="ai-resend-preview-frame"
                          srcDoc={rsPreview.html}
                          className="w-full h-72 bg-white border border-hg-border rounded-sm"
                        />
                      </div>
                    )}

                    {!rsConfirm ? (
                      <button
                        data-testid="ai-resend-send"
                        onClick={() => setRsConfirm(true)}
                        disabled={!rsPreview || !!rsBusy || !to}
                        title={!rsPreview ? 'Preview the email before sending it' : undefined}
                        className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
                      >
                        <Mail size={13} /> Send to {to || '—'}
                      </button>
                    ) : (
                      <div className="border border-hg-green/60 bg-hg-green/10 rounded-sm p-3 space-y-2"
                           data-testid="ai-resend-confirm">
                        <div className="text-xs font-sans text-hg-text">
                          Send the executed copy of <b>{agreement.reference}</b> to{' '}
                          <b>{to}</b>, subject <b>{rsPreview?.subject}</b>? It goes from your
                          own name.
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            data-testid="ai-resend-confirm-yes"
                            onClick={doResend}
                            disabled={!!rsBusy}
                            className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
                          >
                            {rsBusy === 'send' ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                            Yes — send it
                          </button>
                          <button
                            data-testid="ai-resend-confirm-no"
                            onClick={() => setRsConfirm(false)}
                            className="px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm"
                          >
                            Not yet
                          </button>
                        </div>
                      </div>
                    )}

                    {/* THE OUTCOME STAYS PUT — sent with its timestamp, or failed with the
                        reason. Never a navigation that hides which it was. */}
                    {rsResult && (
                      <div
                        data-testid={rsResult.ok ? 'ai-resend-sent' : 'ai-resend-failed'}
                        className={`text-xs font-sans rounded-sm px-3 py-2 border ${
                          rsResult.ok
                            ? 'text-hg-green border-hg-green/40 bg-hg-green/10'
                            : 'text-red-400 border-red-500/40 bg-red-500/10'
                        }`}
                      >
                        {rsResult.ok
                          ? `Sent to ${rsResult.to} at ${rsResult.at}${rsResult.campaign_id ? ` (campaign ${rsResult.campaign_id})` : ''}.`
                          : (rsResult.kind === 'preview'
                              ? `PREVIEW FAILED — ${rsResult.detail}. Nothing was sent.`
                              : `NOT SENT — ${rsResult.detail}`)}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* THE CARD-SETUP LINK. Only for a fitter still waiting on a card: reissuing to an
              active record would mint a live bearer key against somebody whose card is
              already on file, which the server refuses anyway. */}
          {agreement.status === 'approved' && cardLink?.fitter_status === 'awaiting_card' && (
            <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3"
                 data-testid="ai-card-link">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">
                  Card-setup link
                </div>
                <span data-testid="ai-card-link-status"
                  className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${
                    cardLink.status === 'active'
                      ? 'text-hg-green border-hg-green/40 bg-hg-green/10'
                      : 'text-yellow-700 border-yellow-500/40 bg-yellow-400/10'
                  }`}>
                  {cardLink.status}
                </span>
              </div>

              <p className="text-xs font-sans text-hg-text-dim">
                {cardLink.status === 'active' && (
                  <>Live{cardLink.expires_at ? <> until <b className="text-hg-text">{fmtDate(cardLink.expires_at)}</b></> : null}. They add a card themselves; nobody here types card details.</>
                )}
                {cardLink.status === 'expired' && (
                  <>This link <b className="text-hg-text">expired</b>{cardLink.expires_at ? ` on ${fmtDate(cardLink.expires_at)}` : ''}, so their email now leads to a dead page. Send a new one.</>
                )}
                {cardLink.status === 'used' && (
                  <>Already used{cardLink.used_at ? ` on ${fmtDate(cardLink.used_at)}` : ''} — a link is good for one attempt. If the card didn&rsquo;t go through, send a new one.</>
                )}
                {cardLink.status === 'none' && (
                  <>No link on this record. Send one so they can add a card.</>
                )}
              </p>

              <button
                data-testid="ai-card-link-resend"
                onClick={resendCardLink}
                disabled={resending}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green/50 disabled:opacity-40"
              >
                {resending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                {cardLink.status === 'none' ? 'Send a card-setup link' : 'Send a new link'}
              </button>

              {resendNote && (
                <div data-testid="ai-card-link-note"
                  className="text-xs font-sans text-hg-green border border-hg-green/30 bg-hg-green/10 rounded-sm px-3 py-2">
                  {resendNote}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- main module ---

export default function AgreementInbox() {
  const { user } = useAuth();
  const actor = user?.email || 'admin';

  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending_review');
  // ?agreement= from a dashboard row opens that agreement (read once, see deepLink.js).
  const [selectedId, setSelectedId] = useState(() => takeDeepLink(['agreement']).agreement || null);
  const [csOpen, setCsOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(null);   // null = not yet known

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = new URLSearchParams();
      if (status !== 'all') p.set('status', status);
      const qs = p.toString();
      const r = await axios.get(`${API}/agreements${qs ? `?${qs}` : ''}`);
      setAgreements(r.data?.agreements || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load agreements.');
    }
    setLoading(false);
  }, [status]);

  useEffect(() => { fetchAgreements(); }, [fetchAgreements]);

  // Whether approving will actually email the licensee. Read from the server rather than
  // hardcoded, so when the hold lifts this page stops warning about it on its own.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/agreements/executed-email-status`)
      .then((r) => { if (alive) setGateOpen(!!r.data?.enabled); })
      .catch(() => { if (alive) setGateOpen(null); });
    return () => { alive = false; };
  }, []);

  if (selectedId) {
    return (
      <div className="space-y-5" data-testid="agreement-inbox-page">
        <AgreementDetail
          id={selectedId}
          actor={actor}
          onBack={() => setSelectedId(null)}
          onChanged={fetchAgreements}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="agreement-inbox-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">APPROVALS</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Review signed fitter agreements. Approving <b className="text-hg-text">countersigns</b> the
          agreement on Henry-Griffitts&rsquo; behalf{gateOpen === false
            ? ' — but the executed-copy email is currently HELD, so nothing goes to the licensee until you send it'
            : ' and emails the licensee the fully-executed copy'};
          the fitter then moves to awaiting card — nothing enrolls or activates (that&apos;s later).
        </p>
        {/* Said where he READS it, not only after he has pressed approve. A page that states
            a consequence the system is not currently delivering is how somebody approves and
            assumes an email went. */}
        {gateOpen === false && (
          <div className="border border-amber-400/50 bg-amber-400/10 rounded-sm p-3 text-xs font-sans text-amber-800"
               data-testid="ai-gate-closed">
            <b>The executed-copy email is on hold.</b> Approving still countersigns the
            agreement and files the executed PDF — but the licensee is not emailed. Use
            <b> Re-send the executed copy</b> on the approved agreement to send it yourself.
          </div>
        )}
        <button
          data-testid="ai-countersignature-toggle"
          onClick={() => setCsOpen((o) => !o)}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green/50"
        >
          <PenLine size={13} /> {csOpen ? 'Hide' : 'Countersignature'}
        </button>
      </div>

      {csOpen && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
          <CountersignatureSetup />
        </div>
      )}

      {/* Filters */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
        <div className="flex gap-1.5 flex-wrap items-center">
          {FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`ai-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs font-sans rounded-sm border transition-colors ${
                status === s ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
          <button
            onClick={fetchAgreements}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green ml-auto"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-400 font-sans" data-testid="ai-error">{error}</div>}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
      ) : agreements.length === 0 ? (
        <div className="text-center py-12 text-sm text-hg-text-dim font-sans" data-testid="ai-empty">
          No agreements match this filter.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ai-list">
          {agreements.map((a) => (
            <AgreementCard key={a.agreement_id} agreement={a} onOpen={setSelectedId} />
          ))}
        </div>
      )}
    </div>
  );
}
